<template>
    <div>
        <app-bar :title="t('premium_personalized_plans_title')"></app-bar>

        <v-layout>
            <v-progress-linear
                v-if="overlay"
                absolute
                indeterminate
            ></v-progress-linear>

            <card-collection
                :meta="meta"
                :busy="overlay"
                :dense="true"
                @updated="updated"
            >
                <template #filters>
                    <v-toolbar
                        elevation="0"
                        color="transparent"
                        class="mt-7"
                        height="56"
                    >
                        <v-text-field
                            v-model="queries.search"
                            @click:clear="clearSearch"
                            class="input-search md mr-2"
                            clearable
                            outlined
                            dense
                            hide-details
                            prepend-inner-icon="mdi-magnify"
                            :label="t('general_label_search')"
                        ></v-text-field>

                        <v-select
                            v-model="queries.legacy"
                            :items="[
                                { text: '-', value: '' },
                                { text: t('general_text_yes'), value: '1' },
                                { text: t('general_text_no'), value: '0' },
                            ]"
                            class="md mr-2"
                            outlined
                            dense
                            append-icon="mdi-chevron-down"
                            hide-details
                            :label="t('premium_label_legacy')"
                        ></v-select>

                        <v-select
                            v-model="queries.plan_activated"
                            :items="[
                                { text: '-', value: '' },
                                { text: t('general_text_yes'), value: '1' },
                                { text: t('general_text_no'), value: '0' },
                            ]"
                            class="md mr-2"
                            outlined
                            dense
                            append-icon="mdi-chevron-down"
                            hide-details
                            :label="t('premium_label_plan_activated')"
                        ></v-select>

                        <v-select
                            v-model="queries.trial"
                            :items="[
                                { text: '-', value: '' },
                                { text: t('general_text_yes'), value: '1' },
                                { text: t('general_text_no'), value: '0' },
                            ]"
                            class="md mr-2"
                            outlined
                            dense
                            append-icon="mdi-chevron-down"
                            hide-details
                            :label="t('premium_label_trial')"
                        ></v-select>

                        <v-btn
                            @click="search"
                            class="sm mx-5"
                            color="primary"
                        >{{ t('general_action_search') }}</v-btn>

                        <v-spacer></v-spacer>

                        <v-btn
                            v-ripple="false"
                            plain
                            color="orange darken-4"
                            @click="reset"
                        >{{ t('general_action_reset_filters') }}</v-btn>
                    </v-toolbar>
                </template>

                <template #data>
                    <thead>
                    <tr>
                        <th>{{ t('premium_label_user_id') }}</th>
                        <th>{{ t('premium_label_first_name') }}</th>
                        <th>{{ t('premium_label_last_name') }}</th>
                        <th>{{ t('user_label_email_address') }}</th>
                        <th>{{ t('premium_label_country') }}</th>
                        <th>{{ t('premium_label_legacy') }}</th>
                        <th>{{ t('premium_label_eligible_for_30d_trial') }}</th>
                        <th>{{ t('premium_label_plan_activated') }}</th>
                        <th>{{ t('premium_label_trial') }}</th>
                        <th>{{ t('premium_label_product_id') }}</th>
                        <th>{{ t('premium_label_programs_count') }}</th>
                        <th>{{ t('premium_label_program_name') }}</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-if="collection.data.length === 0">
                        <td class="text-center" colspan="12">{{ t('general_text_no_data') }}</td>
                    </tr>
                    <tr v-for="item in collection.data" :key="item.id">
                        <td>
                            <router-link :to="{ name: 'trainees.show', params: { id: item.userId } }">{{ item.userId }}</router-link>
                        </td>
                        <td>{{ item.firstName }}</td>
                        <td>{{ item.lastName }}</td>
                        <td>{{ item.email }}</td>
                        <td>{{ item.country }}</td>
                        <td>{{ item.legacy ? t('general_text_yes') : t('general_text_no') }}</td>
                        <td>{{ item.eligibleFor30dTrial ? t('general_text_yes') : t('general_text_no') }}</td>
                        <td>{{ item.planActivated ? t('general_text_yes') : t('general_text_no') }}</td>
                        <td>{{ item.trial ? t('general_text_yes') : t('general_text_no') }}</td>
                        <td>{{ item.productId }}</td>
                        <td>{{ item.programsCount }}</td>
                        <td>
                            <router-link
                                v-if="item.programId"
                                :to="{ name: 'programs.show', params: { id: item.programId } }"
                                class="program-name-cell"
                                :title="item.programName"
                            >#{{ item.programId }} {{ item.programName }}</router-link>
                            <span v-else>-</span>
                        </td>
                    </tr>
                    </tbody>
                </template>
                <template #export>
                    <v-btn
                        @click="_export"
                        class="exs mt-4 ml-4"
                        color="primary"
                        small
                    >{{ t('general_action_export') }}</v-btn>
                </template>
            </card-collection>
        </v-layout>
    </div>
</template>

<script lang="ts">
import { defineComponent, reactive, computed, onMounted, toRefs } from '@vue/composition-api'
import { useI18n } from 'vue-i18n-composable'
import { useStore } from '@/store'
import { usePremiumHttp } from '@/core/api/premium'
import CardCollection from '@/components/CardCollection.vue'

export default defineComponent({
    components: {
        CardCollection,
    },

    setup() {
        const state = reactive({
            overlay: true,
            meta: undefined,
        })

        const queries = reactive({
            search: <string | undefined>undefined,
            legacy: <string | undefined>undefined,
            plan_activated: <string | undefined>undefined,
            trial: <string | undefined>undefined,
            page: 1,
            per_page: 20,
        })

        const store = useStore()
        const collection = computed(() => store.getters['premiumPersonalizedPlanCollection/get'])

        const capture = () => {
            state.overlay = true
            store.dispatch('premiumPersonalizedPlanCollection/get', { ...queries }).then((data: any) => {
                state.meta = data.meta
                state.overlay = false
            })
        }

        const updated = (updatedQueries: any) => {
            queries.page = updatedQueries.page
            capture()
        }

        const _export = () => {
            state.overlay = true
            usePremiumHttp().exportPersonalizedPlans({ ...queries }).then((data: any) => {
                state.overlay = false
                const e = document.createElement('a')
                e.href = window.URL.createObjectURL(data)
                e.setAttribute('download', 'Personalized plans.csv')
                document.body.appendChild(e)
                e.click()
                document.body.removeChild(e)
            })
        }

        const search = () => capture()

        const clearSearch = () => {
            queries.search = undefined
            capture()
        }

        const reset = () => {
            queries.search = undefined
            queries.legacy = undefined
            queries.plan_activated = undefined
            queries.trial = undefined
            queries.page = 1
            capture()
        }

        onMounted(() => capture())

        return {
            queries,
            collection,
            updated,
            _export,
            search,
            clearSearch,
            reset,
            ...toRefs(state),
            ...useI18n(),
        }
    },
})
</script>

<style scoped>
.program-name-cell {
    display: block;
    max-width: 160px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
</style>
